
import React, { useState, useEffect } from 'react';
import { DISC_QUESTIONS } from './constants';
import { DISCDimension, DISCScore, UserAnswers, EmployeeInfo, AnalysisResult } from './types';
import QuestionBlock from './components/QuestionBlock';
import ResultsChart from './components/ResultsChart';
import { getGeminiAnalysis } from './services/geminiService';

enum AppStep {
  LANDING,
  PRIVACY_NOTICE,
  ONBOARDING,
  TEST,
  LOADING,
  RESULTS
}

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>(AppStep.LANDING);
  const [employee, setEmployee] = useState<EmployeeInfo>({ name: '', email: '', age: '' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<UserAnswers>({});
  const [finalScores, setFinalScores] = useState<DISCScore | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const startTest = (e: React.FormEvent) => {
    e.preventDefault();
    if (employee.name && employee.email && employee.age) {
      setStep(AppStep.TEST);
    }
  };

  const handleSelection = (dimension: DISCDimension) => {
    setAnswers(prev => ({ ...prev, [DISC_QUESTIONS[currentIndex].id]: dimension }));
  };

  const nextQuestion = () => {
    if (currentIndex < DISC_QUESTIONS.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      processResults();
    }
  };

  const processResults = async () => {
    setStep(AppStep.LOADING);
    const scores: DISCScore = { D: 0, I: 0, S: 0, C: 0 };
    Object.values(answers).forEach(dim => scores[dim as DISCDimension]++);
    
    setFinalScores(scores);
    try {
      const result = await getGeminiAnalysis(scores, answers, employee);
      setAnalysis(result);
      setStep(AppStep.RESULTS);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Ocorreu um erro ao processar seus dados.");
      setStep(AppStep.LANDING);
    }
  };

  const progress = Math.round((currentIndex / DISC_QUESTIONS.length) * 100);

  return (
    <div className="min-h-screen bg-[#0b0f19] text-slate-100 flex flex-col p-4 md:p-8 font-sans selection:bg-emerald-500/30">
      
      {(step === AppStep.LANDING || step === AppStep.PRIVACY_NOTICE || step === AppStep.RESULTS) && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-emerald-500/10 blur-[80px] md:blur-[120px] rounded-full"></div>
        </div>
      )}

      {step === AppStep.TEST && (
        <header className="max-w-4xl mx-auto w-full mb-6 md:mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b border-slate-800 pb-4 gap-2">
            <div>
              <p className="text-emerald-500 font-bold text-[10px] md:text-xs uppercase tracking-widest mb-1">
                AVALIANDO: {employee.name.toUpperCase()}
              </p>
              <h1 className="text-2xl md:text-4xl font-serif-elegant text-white">Sessão de Autoconhecimento</h1>
            </div>
            <div className="text-left md:text-right w-full md:w-auto">
              <span className="text-slate-500 text-xs md:text-sm font-medium">Progresso: {progress}%</span>
            </div>
          </div>
          <div className="h-1 bg-slate-800 mt-4 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </header>
      )}

      <main className="flex-1 flex flex-col items-center justify-center relative z-10 w-full">
        
        {step === AppStep.LANDING && (
          <div className="max-w-4xl w-full text-center space-y-6 md:space-y-10 animate-in fade-in zoom-in duration-1000">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 md:p-6 rounded-2xl md:rounded-3xl mb-8 shadow-2xl">
                <p className="text-[10px] md:text-sm font-bold uppercase tracking-widest mb-1">⚠️ Falha na Comunicação</p>
                <p className="opacity-90 text-sm md:text-base">{error}</p>
              </div>
            )}
            <div className="space-y-4">
              <h1 className="text-5xl md:text-8xl font-serif-elegant text-white mb-4 md:mb-6 leading-tight tracking-tighter uppercase">
                Jornada <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">DISC</span>
                <div className="text-lg md:text-3xl text-slate-500 font-sans tracking-widest mt-2 md:mt-4 uppercase font-medium">BY EDILSON MORAIS</div>
              </h1>
              <p className="text-lg md:text-2xl text-slate-400 leading-relaxed font-light max-w-2xl mx-auto px-4">
                Um teste rápido e intuitivo para te ajudar a se conhecer melhor. O resultado chega direto no seu e-mail.
              </p>
            </div>
            <div className="pt-4 md:pt-8">
              <button onClick={() => setStep(AppStep.PRIVACY_NOTICE)} className="group relative px-8 md:px-12 py-4 md:py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl md:rounded-2xl transition-all shadow-[0_0_30px_rgba(34,197,94,0.3)] active:scale-95 text-lg md:text-xl tracking-widest uppercase">
                Iniciar Agora ✓
              </button>
            </div>
          </div>
        )}

        {step === AppStep.PRIVACY_NOTICE && (
          <div className="max-w-3xl w-full text-center space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 px-2">
            <div className="flex flex-col items-center space-y-4 md:space-y-6">
              <div className="w-16 h-16 md:w-20 md:h-20 text-emerald-500">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h1 className="text-3xl md:text-6xl font-serif-elegant text-white tracking-tight">Sua privacidade é respeitada.</h1>
            </div>
            <div className="bg-[#111827]/60 border border-slate-800 rounded-[24px] md:rounded-[40px] p-6 md:p-12 backdrop-blur-xl shadow-2xl space-y-6 text-left">
              <p className="text-slate-300 text-base md:text-lg leading-relaxed text-center font-light">
                As respostas são usadas apenas para gerar seu resultado e enviá-lo por e-mail.
                Depois disso, elas são descartadas.
              </p>
            </div>
            <div className="pt-4 flex flex-col md:flex-row items-center justify-center gap-4">
              <button onClick={() => setStep(AppStep.ONBOARDING)} className="px-8 md:px-12 py-4 md:py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl md:rounded-2xl transition-all shadow-xl active:scale-95 text-base md:text-lg tracking-widest uppercase w-full md:w-auto">
                Tudo certo, vamos continuar
              </button>
              <button onClick={() => setStep(AppStep.LANDING)} className="px-8 md:px-12 py-4 md:py-5 bg-slate-800 text-slate-400 font-bold rounded-xl md:rounded-2xl transition-all text-base md:text-lg tracking-widest uppercase w-full md:w-auto">
                Voltar
              </button>
            </div>
          </div>
        )}

        {step === AppStep.ONBOARDING && (
          <div className="max-w-2xl w-full space-y-6 md:space-y-8 text-center animate-in fade-in slide-in-from-bottom-8 duration-700 px-2">
            <div className="space-y-4 md:space-y-6">
              <h1 className="text-3xl md:text-5xl font-serif-elegant text-white mb-2 md:mb-4">Vamos começar 😊</h1>
              <div className="space-y-4 md:space-y-6 text-slate-300 text-base md:text-lg font-light leading-relaxed">
                <p>Essas informações são usadas apenas para enviar seu resultado.</p>
                <div className="bg-emerald-500/5 border-l-4 border-emerald-500 p-6 md:p-8 rounded-r-[24px] md:rounded-r-[32px] text-left space-y-3 md:space-y-4">
                  <p className="font-medium text-emerald-400 text-sm md:text-base">O teste é rápido e não existe resposta certa ou errada,</p>
                  <p className="font-medium text-emerald-400 text-sm md:text-base">Responda com sinceridade e no seu ritmo.</p>
                  <p className="font-medium text-emerald-400 text-sm md:text-base">Confie na primeira resposta que vier à mente.</p>
                </div>
              </div>
            </div>
            <form onSubmit={startTest} className="bg-[#111827] p-6 md:p-8 rounded-[24px] md:rounded-[32px] border border-slate-800 shadow-2xl space-y-6 text-left">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="space-y-2">
                  <label className="text-slate-400 text-xs md:text-sm font-medium">Nome Completo</label>
                  <input required value={employee.name} onChange={e => setEmployee({...employee, name: e.target.value})} placeholder="Seu nome" className="w-full bg-[#0f172a] border border-slate-800 rounded-xl md:rounded-2xl py-3 md:py-4 px-4 md:px-6 text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm md:text-base" />
                </div>
                <div className="space-y-2">
                  <label className="text-slate-400 text-xs md:text-sm font-medium">Idade</label>
                  <input required value={employee.age} onChange={e => setEmployee({...employee, age: e.target.value})} placeholder="Sua idade" className="w-full bg-[#0f172a] border border-slate-800 rounded-xl md:rounded-2xl py-3 md:py-4 px-4 md:px-6 text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm md:text-base" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-slate-400 text-xs md:text-sm font-medium">E-mail</label>
                <input required type="email" value={employee.email} onChange={e => setEmployee({...employee, email: e.target.value})} placeholder="Para receber o laudo" className="w-full bg-[#0f172a] border border-slate-800 rounded-xl md:rounded-2xl py-3 md:py-4 px-4 md:px-6 text-white focus:ring-2 focus:ring-emerald-500 outline-none text-sm md:text-base" />
              </div>
              <button type="submit" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 md:py-5 rounded-xl md:rounded-2xl transition-all text-base md:text-lg uppercase tracking-widest shadow-xl">Iniciar Teste</button>
            </form>
          </div>
        )}

        {step === AppStep.TEST && (
          <div className="max-w-4xl w-full animate-in fade-in duration-500 px-2">
            <div className="bg-[#111827] p-6 md:p-10 rounded-[24px] md:rounded-[40px] border border-slate-800 shadow-2xl relative">
               <QuestionBlock 
                question={DISC_QUESTIONS[currentIndex]}
                selectedDimension={answers[DISC_QUESTIONS[currentIndex].id] || null}
                onSelect={handleSelection}
               />
            </div>
            <footer className="mt-6 md:mt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-slate-500 text-xs md:text-sm font-medium">Questão {currentIndex + 1} de 15</div>
              <button onClick={nextQuestion} disabled={!answers[DISC_QUESTIONS[currentIndex].id]} className="w-full md:w-auto bg-emerald-500 hover:bg-emerald-600 disabled:opacity-30 text-white font-bold px-8 md:px-12 py-4 rounded-xl md:rounded-2xl text-base md:text-lg uppercase tracking-widest transition-all">
                {currentIndex === DISC_QUESTIONS.length - 1 ? 'Gerar Análise' : 'Próxima'}
              </button>
            </footer>
          </div>
        )}

        {step === AppStep.LOADING && (
          <div className="text-center space-y-4 md:space-y-6 px-4">
            <div className="w-16 h-16 md:w-24 md:h-24 border-4 border-slate-800 border-t-emerald-500 rounded-full animate-spin mx-auto" />
            <h2 className="text-2xl md:text-3xl font-serif-elegant text-white">Analisando seu perfil...</h2>
            <p className="text-slate-400 text-sm md:text-base max-w-sm mx-auto">Quase lá! Estamos preparando seu relatório personalizado.</p>
          </div>
        )}

        {step === AppStep.RESULTS && (
          <div className="max-w-3xl w-full text-center space-y-8 md:space-y-12 animate-in fade-in zoom-in duration-1000 py-8 md:py-12 px-2">
            <div className="space-y-6 md:space-y-8">
              <h1 className="text-3xl md:text-6xl font-serif-elegant text-white leading-tight">
                Obrigado por responder ao teste! 🙌
              </h1>
              
              <div className="bg-[#111827]/80 border border-slate-800 p-6 md:p-12 rounded-[24px] md:rounded-[40px] shadow-2xl backdrop-blur-xl space-y-6 md:space-y-8">
                <p className="text-xl md:text-2xl text-emerald-400 font-medium">
                  Sua participação é muito importante para nós.
                </p>
                
                <div className="space-y-4 md:space-y-6 text-base md:text-lg text-slate-300 leading-relaxed font-light">
                  <p>
                    O resultado da sua análise DISC já foi enviado para o e-mail <span className="text-white font-bold underline decoration-emerald-500/50 break-all">{employee.email}</span> que você cadastrou no início do teste.
                  </p>
                  
                  <div className="bg-slate-900/50 p-4 md:p-6 rounded-2xl md:rounded-3xl border border-slate-800">
                    <p className="text-sm md:text-base">👉 Caso não encontre o e-mail, verifique também a caixa de spam ou promoções.</p>
                  </div>
                  
                  <p className="text-lg md:text-xl italic text-slate-400">
                    Leia com calma — ele foi preparado para te ajudar a se conhecer melhor.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 md:pt-8">
              <button 
                onClick={() => {
                  setStep(AppStep.LANDING);
                  setAnswers({});
                  setCurrentIndex(0);
                  setAnalysis(null);
                }} 
                className="w-full md:w-auto px-8 md:px-12 py-4 md:py-5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl md:rounded-2xl transition-all shadow-[0_0_30px_rgba(34,197,94,0.3)] active:scale-95 text-lg md:text-xl tracking-widest uppercase"
              >
                Refazer Teste
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="py-6 md:py-8 text-center border-t border-slate-800/50 mt-8 md:mt-12">
        <p className="text-slate-500 text-[10px] md:text-sm font-medium tracking-widest uppercase">
          © {new Date().getFullYear()} JORNADA DISC • EDILSON MORAIS
        </p>
      </footer>
    </div>
  );
};

export default App;
